import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Play,
  ArrowUp,
  ArrowDown,
  Minus,
  Target,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  TrendingUp,
  EyeOff,
  Loader,
  Trash2,
  RotateCcw,
  HelpCircle,
  Search,
  Plus,
  Bell,
  Users,
  BarChart3,
  Crosshair,
  Check,
  X,
} from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";
import KeywordDetailModal from "./KeywordDetailModal";

const gapTypeConfig = {
  missing: { label: "Missing", color: "text-red-400", bg: "bg-red-900/20", border: "border-red-800/30", icon: EyeOff, description: "They rank, you don't" },
  behind: { label: "Behind", color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-800/30", icon: ArrowDown, description: "You rank lower" },
  ahead: { label: "Ahead", color: "text-green-400", bg: "bg-green-900/20", border: "border-green-800/30", icon: ArrowUp, description: "You rank higher" },
  overlap: { label: "Overlap", color: "text-blue-400", bg: "bg-blue-900/20", border: "border-blue-800/30", icon: Minus, description: "Similar position" },
};

const TABS = [
  { key: "analysis", label: "Gap Analysis", icon: Target },
  { key: "tracked", label: "Tracked Competitors", icon: Users },
  { key: "rankings", label: "Rankings", icon: BarChart3 },
  { key: "alerts", label: "Alerts", icon: Bell },
];

/* ======== Tracked Competitors Tab ======== */
function TrackedTab({ siteId }) {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "", url: "", notes: "" });
  const [adding, setAdding] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get(`/websites/${siteId}/tracked-competitors/`)
      .then((r) => setCompetitors(r.data.results || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleAdd = () => {
    if (!form.name || !form.domain) return;
    setAdding(true);
    const url = form.url || `https://${form.domain}`;
    api.post(`/websites/${siteId}/tracked-competitors/`, { ...form, url })
      .then((r) => {
        setCompetitors((prev) => [...prev, r.data]);
        setForm({ name: "", domain: "", url: "", notes: "" });
        setShowAdd(false);
      })
      .catch(() => {})
      .finally(() => setAdding(false));
  };

  const handleDelete = (id) => {
    api.delete(`/websites/${siteId}/tracked-competitors/${id}/`)
      .then(() => setCompetitors((prev) => prev.filter((c) => c.id !== id)));
  };

  const handleCheckRankings = () => {
    setChecking(true);
    api.post(`/websites/${siteId}/competitors/check-rankings/`)
      .then(() => setTimeout(() => setChecking(false), 5000))
      .catch(() => setChecking(false));
  };

  if (loading) return <div className="flex justify-center py-12"><Loader className="animate-spin text-copper" size={20} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400">{competitors.length} tracked competitors</span>
        <div className="flex items-center gap-2">
          <button onClick={handleCheckRankings} disabled={checking}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 hover:border-copper/30 text-gray-300 rounded-lg disabled:opacity-50">
            <RefreshCw size={12} className={checking ? "animate-spin" : ""} />
            {checking ? "Checking..." : "Check Rankings"}
          </button>
          <button onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper text-dark rounded-lg font-medium hover:bg-copper/90">
            <Plus size={12} /> Add Competitor
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-dark-300 border border-copper/30 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Company name" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
            <input type="text" placeholder="domain.com" value={form.domain}
              onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
          </div>
          <input type="text" placeholder="Notes (optional)" value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={adding || !form.name || !form.domain}
              className="px-4 py-1.5 bg-copper text-dark rounded-lg text-sm font-medium disabled:opacity-50">
              {adding ? "Adding..." : "Add"}
            </button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-1.5 text-gray-400 text-sm hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {competitors.map((comp) => (
          <div key={comp.id} className="bg-dark-300 border border-dark-50 rounded-xl p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${comp.is_active ? "bg-green-400" : "bg-gray-600"}`} />
                <span className="text-sm font-medium">{comp.name}</span>
                <span className="text-xs text-gray-500 font-mono">{comp.domain}</span>
              </div>
              {comp.notes && <div className="text-xs text-gray-600 mt-1">{comp.notes}</div>}
              <div className="text-xs text-gray-600 mt-1">{comp.ranking_count || 0} keyword rankings tracked</div>
            </div>
            <div className="flex items-center gap-2">
              <a href={comp.url || `https://${comp.domain}`} target="_blank" rel="noopener noreferrer"
                className="text-gray-500 hover:text-copper transition-colors">
                <ExternalLink size={14} />
              </a>
              <button onClick={() => handleDelete(comp.id)}
                className="text-gray-600 hover:text-red-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {!competitors.length && (
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
            <Users size={32} className="mx-auto mb-3 text-gray-600" />
            <p className="text-sm">No tracked competitors yet.</p>
            <p className="text-xs mt-1">Add competitors to monitor their keyword rankings.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======== Rankings Comparison Tab ======== */
function RankingsTab({ siteId }) {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/websites/${siteId}/competitors/comparison/`)
      .then((r) => setComparison(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) return <div className="flex justify-center py-12"><Loader className="animate-spin text-copper" size={20} /></div>;

  if (!comparison || !comparison.comparison?.length) {
    return (
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
        <BarChart3 size={32} className="mx-auto mb-3 text-gray-600" />
        <p className="text-sm">No ranking data yet.</p>
        <p className="text-xs mt-1">Add tracked competitors and set keyword priorities, then check rankings.</p>
      </div>
    );
  }

  const competitorDomains = comparison.competitors?.map((c) => c.domain) || [];

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-dark-50">
              <th className="text-left py-2.5 px-3 font-medium">Keyword</th>
              <th className="text-left py-2.5 px-3 font-medium text-copper">Your Position</th>
              {competitorDomains.map((d) => (
                <th key={d} className="text-left py-2.5 px-3 font-medium">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparison.comparison.map((row) => (
              <tr key={row.keyword_id} className="border-b border-dark-50/50 hover:bg-dark-50/30">
                <td className="py-2.5 px-3 font-mono text-gray-200 text-xs">{row.keyword}</td>
                <td className="py-2.5 px-3 font-mono text-copper">
                  {row.our_position ? `#${Math.round(row.our_position)}` : "—"}
                </td>
                {competitorDomains.map((d) => {
                  const compData = row.competitors[d];
                  const pos = compData?.position;
                  const ahead = pos && row.our_position && pos > row.our_position;
                  const behind = pos && row.our_position && pos < row.our_position;
                  return (
                    <td key={d} className={`py-2.5 px-3 font-mono text-xs ${behind ? "text-red-400" : ahead ? "text-green-400" : "text-gray-400"}`}>
                      {pos ? `#${Math.round(pos)}` : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ======== Alerts Tab ======== */
function AlertsTab({ siteId }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/websites/${siteId}/competitor-alerts/`)
      .then((r) => setAlerts(r.data.results || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const markAllRead = () => {
    api.post(`/websites/${siteId}/competitor-alerts/mark-all-read/`)
      .then(() => setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true }))));
  };

  const markRead = (id) => {
    api.post(`/competitor-alerts/${id}/read/`)
      .then(() => setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, is_read: true } : a)));
  };

  if (loading) return <div className="flex justify-center py-12"><Loader className="animate-spin text-copper" size={20} /></div>;

  const unread = alerts.filter((a) => !a.is_read).length;

  return (
    <div className="space-y-4">
      {unread > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{unread} unread alerts</span>
          <button onClick={markAllRead}
            className="text-xs text-copper hover:underline">Mark all read</button>
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
          <Bell size={32} className="mx-auto mb-3 text-gray-600" />
          <p className="text-sm">No competitor alerts yet.</p>
          <p className="text-xs mt-1">Alerts appear when competitors outrank you or publish new content.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert) => (
            <div key={alert.id}
              className={`bg-dark-300 border rounded-xl p-4 ${alert.is_read ? "border-dark-50 opacity-60" : "border-copper/30"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                      alert.alert_type === "outranked" ? "bg-red-900/20 text-red-400"
                        : alert.alert_type === "new_content" ? "bg-blue-900/20 text-blue-400"
                        : alert.alert_type === "new_backlink" ? "bg-green-900/20 text-green-400"
                        : "bg-amber-900/20 text-amber-400"
                    }`}>
                      {alert.alert_type.replace("_", " ")}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">{alert.competitor_domain}</span>
                    {alert.keyword_text && (
                      <span className="text-xs text-gray-600">for "{alert.keyword_text}"</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">{alert.details}</p>
                  <p className="text-[10px] text-gray-600 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
                </div>
                {!alert.is_read && (
                  <button onClick={() => markRead(alert.id)}
                    className="text-gray-500 hover:text-copper transition-colors shrink-0">
                    <Check size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ======== MAIN COMPONENT ======== */
export default function CompetitorGap({ selectedSite }) {
  const [activeTab, setActiveTab] = useState("analysis");
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [competitorDomain, setCompetitorDomain] = useState("");
  const [gapFilter, setGapFilter] = useState("all");
  const [sortField, setSortField] = useState("opportunity_score");
  const [sortDir, setSortDir] = useState("desc");
  const [showForm, setShowForm] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showGapHelp, setShowGapHelp] = useState(false);
  const [showScoreHelp, setShowScoreHelp] = useState(false);
  const [suggestedCompetitors, setSuggestedCompetitors] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [detailKeyword, setDetailKeyword] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-fill keyword from URL params (e.g. /competitors?keyword=bathroom+renovation)
  useEffect(() => {
    const keyword = searchParams.get("keyword");
    if (keyword) {
      setDetailKeyword(keyword);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load suggested competitors
  useEffect(() => {
    if (!selectedSite) return;
    api.get(`/websites/${selectedSite.id}/competitors/discover/`)
      .then((res) => setSuggestedCompetitors(res.data || []))
      .catch(() => setSuggestedCompetitors([]));
  }, [selectedSite]);

  const runDiscovery = () => {
    if (!selectedSite) return;
    setDiscovering(true);
    api.post(`/websites/${selectedSite.id}/competitors/discover/`)
      .then((res) => setSuggestedCompetitors(res.data || []))
      .catch(() => {})
      .finally(() => setDiscovering(false));
  };

  const analyzeDiscovered = (domain) => {
    setCompetitorDomain(domain);
    setShowForm(false);
    setRunning(true);
    api.post(`/websites/${selectedSite.id}/competitors/run/`, { competitor_domain: domain })
      .then((res) => {
        const newAnalysis = { id: res.data.analysis_id, competitor_domain: domain, status: "running", started_at: new Date().toISOString() };
        setSelectedAnalysis(newAnalysis);
        setAnalyses((prev) => [newAnalysis, ...prev]);
        setKeywords([]);
        setPolling(true);
        setSuggestedCompetitors((prev) => prev.map((c) => c.domain === domain ? { ...c, analyzed: true } : c));
      })
      .catch(() => setRunning(false));
  };

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api.get(`/websites/${selectedSite.id}/competitors/`)
      .then((res) => {
        const list = res.data.results || res.data;
        setAnalyses(list);
        if (list.length > 0) {
          const latest = list[0];
          setSelectedAnalysis(latest);
          if (latest.status === "running") {
            setPolling(true);
          } else {
            return api.get(`/websites/${selectedSite.id}/competitors/${latest.id}/`);
          }
        } else {
          setSelectedAnalysis(null);
          setKeywords([]);
        }
      })
      .then((res) => { if (res && res.data.keywords) setKeywords(res.data.keywords); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite]);

  useEffect(() => {
    if (!polling || !selectedAnalysis || !selectedSite) return;
    const interval = setInterval(() => {
      api.get(`/websites/${selectedSite.id}/competitors/${selectedAnalysis.id}/`)
        .then((res) => {
          setSelectedAnalysis(res.data);
          if (res.data.status !== "running") {
            setPolling(false);
            setRunning(false);
            if (res.data.keywords) setKeywords(res.data.keywords);
            api.get(`/websites/${selectedSite.id}/competitors/`).then((r) => setAnalyses(r.data.results || r.data));
          }
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, selectedAnalysis, selectedSite]);

  const runAnalysis = () => {
    if (!selectedSite || !competitorDomain.trim()) return;
    setRunning(true);
    api.post(`/websites/${selectedSite.id}/competitors/run/`, { competitor_domain: competitorDomain.trim() })
      .then((res) => {
        const newAnalysis = { id: res.data.analysis_id, competitor_domain: competitorDomain.trim(), status: "running", started_at: new Date().toISOString() };
        setSelectedAnalysis(newAnalysis);
        setAnalyses((prev) => [newAnalysis, ...prev]);
        setKeywords([]);
        setPolling(true);
        setShowForm(false);
        setCompetitorDomain("");
      })
      .catch(() => setRunning(false));
  };

  const deleteAnalysis = (e, analysis) => {
    e.stopPropagation();
    if (!selectedSite) return;
    api.delete(`/websites/${selectedSite.id}/competitors/${analysis.id}/`)
      .then(() => {
        const remaining = analyses.filter((a) => a.id !== analysis.id);
        setAnalyses(remaining);
        if (selectedAnalysis?.id === analysis.id) {
          if (remaining.length > 0) loadAnalysis(remaining[0]);
          else { setSelectedAnalysis(null); setKeywords([]); }
        }
      });
  };

  const rerunAnalysis = (e, analysis) => {
    e.stopPropagation();
    if (!selectedSite) return;
    setRunning(true);
    api.post(`/websites/${selectedSite.id}/competitors/${analysis.id}/rerun/`)
      .then((res) => {
        const newAnalysis = { id: res.data.analysis_id, competitor_domain: analysis.competitor_domain, status: "running", started_at: new Date().toISOString() };
        setSelectedAnalysis(newAnalysis);
        setAnalyses((prev) => [newAnalysis, ...prev]);
        setKeywords([]);
        setPolling(true);
      })
      .catch(() => setRunning(false));
  };

  const loadAnalysis = (analysis) => {
    if (analysis.status === "running") {
      setSelectedAnalysis(analysis);
      setKeywords([]);
      setPolling(true);
      return;
    }
    setLoading(true);
    api.get(`/websites/${selectedSite.id}/competitors/${analysis.id}/`)
      .then((res) => { setSelectedAnalysis(res.data); setKeywords(res.data.keywords || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir(field === "opportunity_score" ? "desc" : "asc"); }
  };

  const filtered = useMemo(() => {
    let data = [...keywords];
    if (gapFilter !== "all") data = data.filter((k) => k.gap_type === gapFilter);
    data.sort((a, b) => {
      const aVal = a[sortField] ?? Infinity;
      const bVal = b[sortField] ?? Infinity;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return data;
  }, [keywords, gapFilter, sortField, sortDir]);

  const gapCounts = useMemo(() => {
    const counts = { missing: 0, behind: 0, ahead: 0, overlap: 0 };
    keywords.forEach((k) => { if (counts[k.gap_type] !== undefined) counts[k.gap_type]++; });
    return counts;
  }, [keywords]);

  const SortHeader = ({ field, children }) => (
    <th className="text-left py-2.5 px-3 font-medium cursor-pointer hover:text-copper transition-colors select-none"
      onClick={() => toggleSort(field)}>
      <span className="flex items-center gap-1">
        {children}
        {sortField === field && (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </span>
    </th>
  );

  if (!selectedSite) return <div className="text-gray-500 text-center py-20">Select a website to analyze competitors</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-mono">
          <span className="text-copper">Competitor</span> Intelligence
        </h1>
      </div>

      {/* Tab navigation */}
      <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"
              }`}>
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tracked competitors tab */}
      {activeTab === "tracked" && <TrackedTab siteId={selectedSite.id} />}

      {/* Rankings comparison tab */}
      {activeTab === "rankings" && <RankingsTab siteId={selectedSite.id} />}

      {/* Alerts tab */}
      {activeTab === "alerts" && <AlertsTab siteId={selectedSite.id} />}

      {/* Gap Analysis tab (original) */}
      {activeTab === "analysis" && (
        <>
          <div className="flex items-center gap-2 justify-end">
            <button onClick={runDiscovery} disabled={discovering}
              className="flex items-center gap-2 px-4 py-2 bg-dark-300 border border-dark-50 hover:border-copper/30 text-gray-300 text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
              <Search size={14} />
              {discovering ? "Discovering..." : "Discover Competitors"}
            </button>
            <button onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors">
              <Target size={14} />
              New Analysis
            </button>
          </div>

          {/* Suggested Competitors */}
          {suggestedCompetitors.length > 0 && (
            <div className="bg-dark-300 border border-copper/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Search size={16} className="text-copper" />
                <span className="text-sm font-medium text-copper">Suggested Competitors</span>
              </div>
              <div className="space-y-2">
                {suggestedCompetitors.map((comp) => (
                  <div key={comp.domain} className="flex items-center justify-between bg-dark/50 rounded-lg px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-gray-200">{comp.domain}</span>
                      <span className="text-xs text-gray-500">{comp.overlap_count} overlapping keywords ({comp.overlap_pct}%)</span>
                    </div>
                    {comp.analyzed ? (
                      <span className="text-xs text-green-400">Analyzed</span>
                    ) : (
                      <button onClick={() => analyzeDiscovered(comp.domain)} disabled={running}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50">
                        <Play size={10} /> Analyze
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {showForm && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <div className="text-sm font-medium mb-3">Enter competitor domain</div>
              <div className="flex gap-3">
                <input type="text" placeholder="e.g. competitor.com" value={competitorDomain}
                  onChange={(e) => setCompetitorDomain(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runAnalysis()}
                  className="flex-1 bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50 font-mono" />
                <button onClick={runAnalysis} disabled={running || !competitorDomain.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-50">
                  <Play size={14} /> {running ? "Running..." : "Analyze"}
                </button>
              </div>
            </div>
          )}

          {/* Past analyses */}
          {analyses.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 font-medium">Past Analyses</div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {analyses.map((a) => (
                  <div key={a.id} onClick={() => loadAnalysis(a)}
                    className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer group ${
                      selectedAnalysis?.id === a.id ? "bg-copper/20 text-copper" : "bg-dark-300 text-gray-400 hover:text-white"
                    }`}>
                    <span className="font-mono">{a.competitor_domain}</span>
                    <span className="text-gray-500">
                      {a.status === "running" ? <Loader size={10} className="inline animate-spin" />
                        : a.status === "failed" ? <AlertCircle size={10} className="inline text-red-400" />
                        : new Date(a.started_at).toLocaleDateString()}
                    </span>
                    {a.status !== "running" && (
                      <span className="flex items-center gap-1 ml-1">
                        <button onClick={(e) => rerunAnalysis(e, a)} className="p-0.5 rounded hover:bg-copper/20 text-gray-500 hover:text-copper"><RotateCcw size={11} /></button>
                        <button onClick={(e) => deleteAnalysis(e, a)} className="p-0.5 rounded hover:bg-red-900/30 text-gray-500 hover:text-red-400"><Trash2 size={11} /></button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content area */}
          {loading ? (
            <div className="animate-pulse space-y-4"><div className="h-24 bg-dark-300 rounded-xl" /><div className="h-96 bg-dark-300 rounded-xl" /></div>
          ) : !selectedAnalysis ? (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
              <Target size={40} className="mx-auto mb-4 text-gray-600" />
              <div className="text-sm">No analyses yet. Click "New Analysis" to compare.</div>
            </div>
          ) : selectedAnalysis.status === "running" ? (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center">
              <RefreshCw size={32} className="mx-auto mb-4 text-copper animate-spin" />
              <div className="text-sm text-gray-300">Analyzing {selectedAnalysis.competitor_domain}...</div>
              <div className="text-xs text-gray-500 mt-2">This may take a few minutes.</div>
            </div>
          ) : selectedAnalysis.status === "failed" ? (
            <div className="bg-red-900/10 border border-red-800/30 rounded-xl p-6">
              <div className="flex items-center gap-2 text-red-400 mb-2"><AlertCircle size={16} /><span className="font-medium text-sm">Analysis Failed</span></div>
              <div className="text-xs text-gray-400">{selectedAnalysis.error_message || "An unexpected error occurred."}</div>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 flex items-center justify-center">
                  <ScoreRing score={Math.round(keywords.length > 0 ? keywords.reduce((sum, k) => sum + k.opportunity_score, 0) / keywords.length : 0)} label="Avg Score" size={90} />
                </div>
                {[
                  { label: "Our Keywords", value: selectedAnalysis.our_keywords_count, color: "text-copper" },
                  { label: "Competitor Keywords", value: selectedAnalysis.competitor_keywords_count },
                  { label: "Gap (Missing)", value: selectedAnalysis.gap_keywords_count, color: "text-red-400" },
                  { label: "Overlap", value: selectedAnalysis.overlap_keywords_count, color: "text-blue-400" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-dark-300 border border-dark-50 rounded-xl p-5">
                    <div className="text-xs text-gray-500 mb-1">{label}</div>
                    <div className={`text-2xl font-mono font-bold ${color || "text-white"}`}>{value ?? 0}</div>
                  </div>
                ))}
              </div>

              {/* Gap type filter */}
              <div className="flex gap-2">
                {["all", "missing", "behind", "ahead", "overlap"].map((type) => {
                  const config = gapTypeConfig[type];
                  return (
                    <button key={type} onClick={() => setGapFilter(type)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        gapFilter === type ? "bg-copper/20 text-copper" : "bg-dark-300 text-gray-400 hover:text-white"
                      }`}>
                      {type === "all" ? "All" : config?.label}
                      {type !== "all" && <span className="ml-1.5 text-gray-500">{gapCounts[type] || 0}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Table */}
              <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b border-dark-50">
                        <SortHeader field="keyword">Keyword</SortHeader>
                        <th className="text-left py-2.5 px-3 font-medium">Gap Type</th>
                        <SortHeader field="our_position">Our Pos.</SortHeader>
                        <SortHeader field="competitor_position">Comp. Pos.</SortHeader>
                        <SortHeader field="opportunity_score">Opportunity</SortHeader>
                        <th className="text-left py-2.5 px-3 font-medium">Action</th>
                        <th className="py-2.5 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((kw, i) => {
                        const config = gapTypeConfig[kw.gap_type] || gapTypeConfig.overlap;
                        const GapIcon = config.icon;
                        return (
                          <tr key={i} className="border-b border-dark-50/50 hover:bg-dark-50/30">
                            <td className="py-2.5 px-3 font-mono text-gray-200">
                              <button onClick={() => setDetailKeyword(kw.keyword)} className="hover:text-copper text-left">{kw.keyword}</button>
                            </td>
                            <td className="py-2.5 px-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                                <GapIcon size={10} /> {config.label}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 font-mono">
                              {kw.our_position != null ? <span className="text-gray-300">#{Math.round(kw.our_position)}</span> : <span className="text-gray-600">—</span>}
                            </td>
                            <td className="py-2.5 px-3 font-mono">
                              {kw.competitor_position != null ? <span className="text-gray-300">#{Math.round(kw.competitor_position)}</span> : <span className="text-gray-600">—</span>}
                            </td>
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-dark rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${kw.opportunity_score >= 70 ? "bg-green-400" : kw.opportunity_score >= 40 ? "bg-amber-400" : "bg-gray-500"}`}
                                    style={{ width: `${kw.opportunity_score}%` }} />
                                </div>
                                <span className={`text-xs font-mono font-medium ${kw.opportunity_score >= 70 ? "text-green-400" : kw.opportunity_score >= 40 ? "text-amber-400" : "text-gray-500"}`}>
                                  {kw.opportunity_score}
                                </span>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-xs text-gray-400 min-w-[180px]">{kw.suggested_action}</td>
                            <td className="py-2.5 px-3">
                              {kw.competitor_url && (
                                <a href={kw.competitor_url} target="_blank" rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-copper"><ExternalLink size={12} /></a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!filtered.length && (
                        <tr><td colSpan={7} className="py-12 text-center text-gray-500">
                          {keywords.length ? "No keywords match this filter" : "No keywords found"}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {detailKeyword && <KeywordDetailModal keyword={detailKeyword} selectedSite={selectedSite} onClose={() => setDetailKeyword(null)} />}
    </div>
  );
}
